import { omit } from "lodash"
import { load, store, getStoreByKeyValue, localStorageGetItem } from "./localStorage"
import { errorsManager } from "../../../../lib/common/js/services/errors/errorsManager"
import { getOfferId, getPromoCode, getPaymentQueryString, isFirstPage } from "./helpers"
import { isFunnelFoxPipeline } from "../../../../lib/common/js/funnelFox"
import { TrackingManager } from "../../../../lib/common/js/services/tracking/trackingManager"

const KGS_IN_LBS = 0.453592
const LBS_IN_KG = 2.20462
const INCH_IN_CM = 2.54
const CM_IN_FT = 30.48

export const PRICE_TYPES = {
  TRIAL: "trial",
  EXIT_INTENT_TRIAL: "exit_intent_trial", // not yet implemented for PPOs
  STANDARD: "standard",
  EXIT_INTENT: "exit_intent",
}
export const GOALS = {
  WEIGHT_LOSS: "Lose weight",
  LEAN_AND_LOSE: "Get leaner & lose extra pounds",
  MUSCLE_GAIN: "Gain muscle",
  MAINTAIN: "Develop healthy habits",
}
export const kgToLbs = (kg) => Math.round(kg / KGS_IN_LBS)
export const lbsToKg = (lbs) => Math.round(lbs / LBS_IN_KG)
export const cmToFtIn = (cm) => {
  const totalInch = cm / INCH_IN_CM
  const feet = Math.floor(totalInch / 12)
  const inch = totalInch - 12 * feet

  return { heightFt: Math.round(feet), heightIn: Math.round(inch) }
}
export const ftInToCm = ({ ft, inch }) => {
  const heightCm = Math.ceil(ft * CM_IN_FT + inch * INCH_IN_CM)

  return heightCm
}
export const round = Math.round

const getDefaultOffer = () => {
  const basicOffers = pipelineConfig.offers.filter(
    (offer) => offer.type && offer.type.toLowerCase() === "basic",
  )

  // If single offer pipe
  if (basicOffers.length === 1) return basicOffers[0]

  // If multiple offers pipe
  return basicOffers.find((offer) => offer.customData.default_select === "true")
}

// Used for funnelfox pipelines
const funnelFoxParams = {
  ffSessionId: null,
  ffOrigin: null,
  ffOriginPath: null,
  ffOfferPPOId: null,
  ffExitIntentPath: null,
}

function initialState() {
  return {
    ...funnelFoxParams,
    usePaymentsApi: true,
    token: null,
    domain: null,
    measurementSystem: pipelineConfig.measurementSystem,
    weightKg: null,
    weightLbs: null,
    age: null,
    heightCm: null,
    heightFt: null,
    heightIn: null,
    gender: null,
    firstName: null,
    goal: GOALS.WEIGHT_LOSS,
    targetWeightKg: null,
    targetWeightLbs: null,
    goalPaceKg: null,
    goalPaceLbs: null,
    sleep: null,
    diets: null,
    glassesOfWater: null,
    importantEvent: null,
    importantEventDate: null,
    medication: null,
    deseases: null,
    marketingAgreed: null,
    weightLossReceive: null,
    email: null,
    physicallyActive: null,
    ageRange: null,
    yourGoal: null,
    bodyType: null,
    familiarWithFasting: null,
    hungerTime: null,
    breakfastTime: null,
    lunchTime: null,
    dinnerTime: null,
    cookies: null,
    cookiePolicyBannerAccepted: null,
    fastingWeekends: null,
    mealPrep: null,
    willToLoseWeight: null,
    workingHours: null,
    describeJob: null,
    exitIntent: false,
    showDiscountModal: false,
    isAbandonedCartViewed: false,
    activeOffer: getDefaultOffer(),
    purchasedBasicOffer: null,
    skippedUpsellOffers: [],
    paymentMethod: null,
    paymentSuccessUrl: "",
    paymentFailUrl: "",
    selectedChatNutrionist: null,
    segmentUserId: null,
    trialExitIntent: false,
  }
}

export const LOCAL_STORAGE_KEY = "data"

export default {
  internalTracker: new TrackingManager(
    window.trackingConfig.enabledTrackingServices,
    pipelineConfig,
  ),

  init() {
    this.values = initialState()

    this.reload()

    this.storeAnalytics()

    Alpine.effect(() => {
      this.writeToLocalStorage()
    })
  },

  reload() {
    const data = this.readFromLocalStorage()

    if (data?.values) {
      this.values = data.values
    }
  },

  async trackViewAndMaybeStatus(status, isStoreDataChangedNeeded) {
    if (!status) return // Because of order update on each step. WE only update once status is changed or manually pushed data on click
    if (status) this.values.status = status
    // Add condition for new Segment implementation, cause we need get segmentUserId into localStorage - not to call API once again
    if (isStoreDataChangedNeeded) {
      this.push()
    } else {
      this.push(true)
    }
  },

  storeAnalytics() {
    const analyticsData = {}
    const cookiesData = {}

    analyticsData.domain = document.location.host

    // Add cookies - _ga, _gid, _fbc, _fbp, ...
    document.cookie.split("; ").forEach((item) => {
      const [key, value] = item.split("=")
      analyticsData[key] = value
      cookiesData[key] = value
    })

    // Add search params - fbclid, utm_*, ...
    const url = new URL(document.location)
    url.searchParams.forEach((value, key) => {
      analyticsData[key] = value
    })

    // Add FraudNet data
    analyticsData["fraud_net_session_id"] = localStorageGetItem("fraud_net_session_id") || ""

    delete analyticsData[""]

    // Add global data for Payments (override only if values are present due to init on each page)
    const queryString = window.location.search
    if (queryString) {
      analyticsData["query_string"] = queryString
    }
    if (cookiesData && Object.keys(cookiesData).length > 0) {
      analyticsData.cookies = { ...(analyticsData.cookies || {}), ...cookiesData }
    }
    // Set origin query string only on landing page
    const currentPagePath = window.location.pathname
    if (queryString && isFirstPage(currentPagePath, pipelineConfig)) {
      if (!this.values.origin_query_string) {
        this.values.origin_query_string = queryString
      }
    }

    this.values = { ...this.values, ...analyticsData }
  },

  // Needed so that head.njk can access the function
  isFunnelFoxPipe() {
    return isFunnelFoxPipeline()
  },

  getGA4SessionId() {
    try {
      const cookies = document.cookie
        .split("; ")
        .find((item) => item.includes("_ga_"))
        .split("=")[1]
      return cookies.split(".")[2]
    } catch (e) {
      return
    }
  },

  getProgressWidth(activeStepNumber) {
    if (activeStepNumber < 12) {
      return (activeStepNumber * 2.7).toFixed(2)
    }

    if (activeStepNumber < 19) {
      return (activeStepNumber * 3.53).toFixed(2)
    }

    if (activeStepNumber > 18 && activeStepNumber < 22) {
      return "85"
    }

    if (activeStepNumber == 22) {
      return "100"
    }
  },

  handleCheckoutRedirect(nextStepPath) {
    if (this.isG2Variant() && !this.values.trialExitIntent && this.values.exitIntent) {
      this.values.trialExitIntent = true
    }
    this.values.showDiscountModal = true
    this.values.exitIntent = true
    this.clearPrimerSession()

    if (this.values.token)
      return window.location.replace(`${nextStepPath}#stored-token=${this.values.token}`)

    window.location.replace(nextStepPath)
  },

  async setActiveOffer(changedOffer) {
    const newActiveOffer = pipelineConfig.offers.find(
      (offer) => getOfferId(offer) === getOfferId(changedOffer),
    )
    if (!newActiveOffer) return

    this.values.activeOffer = newActiveOffer
    this.usePreloadedPrimerSession(newActiveOffer)
  },

  // Push data store to backend API
  async push(withPath = false, skippedOfferId = null, onError = null) {
    const apiUrl = pipelineConfig.orderApiUrl
    const options = {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        ...this.values,
        ...(withPath && { path: window.location.pathname }),
        ...(skippedOfferId && { skippedOfferId }),
      }),
    }

    if (this.values.token) {
      options.method = "PATCH"
      options.headers["x-token"] = this.values.token
    }

    try {
      const res = await fetch(apiUrl, options)
      const data = await res.json()
      if (data.token) {
        this.values.token = data.token
      }

      if (withPath || skippedOfferId) return

      if (res.ok) {
        this.error = undefined

        Object.entries(data).forEach(([key, value]) => {
          if (key !== "path") this.values[key] = value
        })
      } else {
        if (onError) onError(data)
        this.error = data.error
      }
    } catch (error) {
      if (withPath) return

      this.error = error
    }

    return !this.error
  },

  readFromLocalStorage() {
    return load(LOCAL_STORAGE_KEY)
  },

  writeToLocalStorage() {
    store(LOCAL_STORAGE_KEY, { values: this.values })
  },

  isAbandonedBasket() {
    return pipelineConfig.variants[pipelineConfig.variant].isAbandonedBasket
  },

  getPageVersion(path) {
    if (!pipelineConfig || !pipelineConfig.pageVersions) return null

    const fullPath = path || window.location.pathname

    let strippedPath = fullPath
    if (pipelineConfig.pathPrefix) {
      strippedPath = fullPath.replace(pipelineConfig.pathPrefix, "")
    }

    const normalizedPath = strippedPath.replace(/\/+$/, "")

    const matchedVersion = Object.entries(pipelineConfig.pageVersions).find(
      ([key]) => normalizedPath === `/${key}` || normalizedPath === key,
    )

    if (matchedVersion) {
      return matchedVersion[1]
    }
    return null
  },

  getOrderByToken(searchParams) {
    return fetch(`${pipelineConfig.orderApiUrl}?${searchParams.toString()}`, {
      method: "GET",
      headers: {
        "content-type": "application/json",
        "x-token": this.values.token,
      },
    })
  },

  getOrderByFFSessionToken(searchParams) {
    try {
      const pipelineUrl = pipelineConfig.orderApiUrl.split("/order")[0]
      const ffSessionUrl = `${pipelineUrl}/ff-orders/${
        this.values.ffSessionId
      }?${searchParams.toString()}`

      return fetch(ffSessionUrl, {
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      })
    } catch (e) {
      console.error("Error fetching order by FF session token:", e)
      return Promise.reject(e)
    }
  },

  // Fetch data from backend API
  async pull(waitForLoginUrl = false, ignoreOffer = false, firstFFFetch = false) {
    let omitKeys = [
      "path",
      "showDiscountModal",
      "exitIntent",
      "trialExitIntent",
      "cookiePolicyBannerAccepted",
    ]
    if (ignoreOffer) {
      omitKeys.push("activeOffer", "primerSession", "preloadedPrimerSessions")
    }

    const searchParams = new URLSearchParams()
    if (waitForLoginUrl) {
      searchParams.append("waitForLoginUrl", "true")
    }
    // token for CPPv3 || ffSessionId for funnelfox
    if (this.values.token || this.values.ffSessionId) {
      try {
        let res
        if (firstFFFetch) {
          res = await this.getOrderByFFSessionToken(searchParams)
        } else {
          res = await this.getOrderByToken(searchParams)
        }

        let data = await res.json()
        data = omit(data, omitKeys)

        if (res.ok) {
          this.error = undefined
          // Funnelfox doesn't need a initial state, but the previous variants, and base variants were working with it
          if (!isFunnelFoxPipeline()) {
            this.values = initialState()
          }
          // Needed, otherwise we don't have initial values coming from FF
          if (firstFFFetch) {
            const prevActiveOffer = this.values.activeOffer
            const initialValues = initialState()
            const prevFFValues = {
              ffSessionId: this.values.ffSessionId,
              ffOrigin: this.values.ffOrigin,
              ffOriginPath: this.values.ffOriginPath,
              ffOfferPPOId: this.values.ffOfferPPOId,
              ffExitIntentPath: this.values.ffExitIntentPath,
            }
            this.values = {
              ...initialValues,
              ...prevFFValues,
              activeOffer: prevActiveOffer,
            }
          }

          Object.entries(data).forEach(([key, value]) => {
            this.values[key] = value
          })

          // TODO: Needed because we don't need to identify user within send-off page, once user click goToLogin.
          // Cause it makes unnecessary API call. Refactor later
          if (!waitForLoginUrl) {
            // Init Klaviyo user and track page, cause u can start tracking after init only. Not to miss any events ^^ (due to AC email)
            this.internalTracker.isUserIdentified({}, "klaviyo").then((isIdentified) => {
              if (!isIdentified) {
                this.internalTracker.trackIdentify({
                  customData: { email: this.values.email },
                  destinationService: "klaviyo",
                  callBackFn: () => {
                    this.internalTracker.trackPageView({}, "klaviyo")
                  },
                })
              }
            })
          }
        } else {
          this.error = data.error
        }

        // AC is treated as a separate standalone pipe
        // with it's own set of offers and discounts.
        // Historically these fields are stored from
        // the main pipe, so we need to reset them in AC.
        if (this.isAbandonedBasket()) {
          this.values.activeOffer = null
          this.values.exitIntent = false
          this.values.showDiscountModal = false
        }

        this.clearPrimerSession()
        this.writeToLocalStorage()
      } catch (error) {
        this.error = error

        const customError = new Error("[Common] Error during data pulling")
        const metadata = {
          originalError: error.message,
          originalStack: error.stack,
        }
        errorsManager.reportError(customError, metadata)
      }
    }
  },

  get normalizedHeight() {
    if (this.isMetric) {
      return this.values.heightCm
    } else {
      return this.values.heightFt * 30.48 + this.values.heightIn * 2.54
    }
  },

  get normalizedWeight() {
    if (this.isMetric) {
      return this.values.weightKg
    } else {
      return this.values.weightLbs * KGS_IN_LBS
    }
  },

  get isFemale() {
    return this.values.gender === "female"
  },

  get isMale() {
    return this.values.gender === "male"
  },

  gender(value) {
    this.values.gender = value
  },

  get weightUnits() {
    const kgTranslated = weightTranslations["kg"]
    const lbsTranslated = weightTranslations["lbs"]

    return this.isMetric ? kgTranslated : lbsTranslated
  },

  get isMetric() {
    return this.values.measurementSystem === "metric"
  },

  get isImperial() {
    return this.values.measurementSystem === "imperial"
  },

  get loseWeight() {
    return this.values.goal === "Lose weight"
  },

  get bmi() {
    return round(this.normalizedWeight / (this.normalizedHeight / 100.0) ** 2)
  },

  get overWeight() {
    return this.normalizedWeight - this.idealWeight
  },

  get idealWeight() {
    return Math.max(round((this.normalizedHeight ** 2 * 21.1) / 10000), 30)
  },

  get idealWeightRange() {
    const minBMI = 18.5
    const maxBMI = 27
    const lowestNormalizedWeightValue = 30

    let min = minBMI * Math.pow(this.normalizedHeight / 100, 2)
    let max = maxBMI * Math.pow(this.normalizedHeight / 100, 2)

    if (min >= max) {
      min = max - 1
    }

    min = Math.max(round(min), lowestNormalizedWeightValue)
    max = Math.max(round(max), lowestNormalizedWeightValue)

    if (this.isImperial) {
      min = kgToLbs(min)
      max = kgToLbs(max)
    }

    min = round(min)
    max = round(max)

    return Array.from({ length: max - min + 1 }, (_, i) => i + min)
  },

  get targetWeight() {
    return this.isMetric ? this.values.targetWeightKg : this.values.targetWeightLbs
  },

  formattedHeight() {
    const { heightCm, heightFt, heightIn } = this.values

    if (this.isImperial) {
      return pipelineConfig.dir === "rtl"
        ? `in${heightIn} ft${heightFt}`
        : `${heightFt}ft ${heightIn}in`
    } else {
      return pipelineConfig.dir === "rtl" ? `cm${heightCm}` : `${heightCm}cm`
    }
  },

  formatWeight(value) {
    return `${value}${this.weightUnits}`
  },

  formattedWeight() {
    return this.formatWeight(this.isMetric ? this.values.weightKg : this.values.weightLbs)
  },

  formattedIdealWeight(rawValue = false) {
    if (rawValue) {
      return this.isMetric ? this.idealWeight : round(kgToLbs(this.idealWeight))
    } else {
      return this.formatWeight(this.isMetric ? this.idealWeight : round(kgToLbs(this.idealWeight)))
    }
  },

  selectedWeight() {
    if (this.targetWeight && this.idealWeightRange.includes(this.targetWeight)) {
      return this.targetWeight
    } else {
      return this.formattedIdealWeight(true)
    }
  },

  formattedMinIdealWeight() {
    return this.formatWeight(this.idealWeightRange[0])
  },

  formattedMaxIdealWeight() {
    return this.formatWeight(this.idealWeightRange.slice(-1)[0])
  },

  formattedWeightToLose() {
    const { weightKg, targetWeightKg, weightLbs, targetWeightLbs } = this.values

    return this.formatWeight(
      this.isMetric ? weightKg - targetWeightKg : weightLbs - targetWeightLbs,
    )
  },

  formattedWeightGoal() {
    return this.formatWeight(
      this.isMetric ? this.values.targetWeightKg : this.values.targetWeightLbs,
    )
  },

  usePreloadedPrimerSession(offer) {
    if (!this.values.preloadedPrimerSessions) return

    this.values.primerSession = this.values.preloadedPrimerSessions[getOfferId(offer)]
  },

  async preloadPrimerSessions() {
    const basicOffers = pipelineConfig.offers.filter(
      (offer) => offer.type && offer.type.toLowerCase() === "basic",
    )

    if (basicOffers.length !== 1) return

    this.preloadPromise = Promise.all(
      basicOffers.map(async (basicOffer) => {
        const primerSession = await this.callPaymentsApi(basicOffer, true)

        return [getOfferId(basicOffer), primerSession]
      }),
    )

    const result = await this.preloadPromise

    try {
      this.values.preloadedPrimerSessions = result.reduce(
        (acc, cur) => ({
          ...acc,
          [cur[0]]: cur[1],
        }),
        {},
      )

      this.usePreloadedPrimerSession(this.values.activeOffer)
      await this.push()
    } catch (error) {
      console.error(error)
    }
  },

  isBasicOffer(offer = this.values.activeOffer) {
    return offer?.type?.toLowerCase() === "basic"
  },

  isBasicExitIntentOffer(offer = this.values.activeOffer) {
    return offer?.type?.toLowerCase() === "basic_exit_intent"
  },

  isOfferTrial(offer = this.values.activeOffer) {
    return (
      Array.isArray(offer.prices) &&
      offer.prices.some((price) =>
        [PRICE_TYPES.TRIAL, PRICE_TYPES.EXIT_INTENT_TRIAL].includes(price.type),
      )
    )
  },

  getPriceWithoutDiscountKey(isTrialOffer, isExitIntent) {
    if (isTrialOffer && isExitIntent) return "trial_price_without_discount"
    if (isTrialOffer) return "trial_price_without_discount"
    if (isExitIntent) return "total_price_without_discount"

    return "total_price_without_discount"
  },

  getOfferOldPrice(offer = this.values.activeOffer) {
    const isTrialOffer = this.isOfferTrial(offer)
    const { exitIntent } = this.values
    const priceKey = this.getPriceWithoutDiscountKey(isTrialOffer, exitIntent)
    const oldPrice = offer.customData[priceKey]

    return this.getFormattedPrice(oldPrice)
  },

  getPriceType(offer = this.values.activeOffer) {
    const isTrialOffer = this.isOfferTrial(offer)
    const { exitIntent } = this.values

    if (!this.isBasicOffer(offer) && !this.isBasicExitIntentOffer(offer)) {
      return PRICE_TYPES.STANDARD
    }

    if (this.isBasicExitIntentOffer(offer)) return PRICE_TYPES.TRIAL
    if (isTrialOffer && exitIntent) return PRICE_TYPES.EXIT_INTENT_TRIAL
    if (isTrialOffer) return PRICE_TYPES.TRIAL
    if (exitIntent) return PRICE_TYPES.EXIT_INTENT

    return PRICE_TYPES.STANDARD
  },

  getFormattedPrice(amount = "0", symbol = this.values.activeOffer.customData.symbol) {
    return `${symbol}${amount}`
  },

  getPriceProperty(priceType, property, offer = this.values.activeOffer) {
    return offer.prices?.find((price) => price.type === priceType)?.[property] || null
  },

  getFormattedTotalPrice(offer = this.values.activeOffer) {
    const { total_price = "0" } = offer.customData
    const isBasicOffer = this.isBasicOffer(offer) || this.isBasicExitIntentOffer(offer)
    const priceType = isBasicOffer ? this.getPriceType(offer) : PRICE_TYPES.STANDARD
    const price = this.getPriceProperty(priceType, "amount", offer) || total_price

    return this.getFormattedPrice(price)
  },

  getFormattedPriceRecurring(offer = this.values.activeOffer) {
    const { customData } = offer
    const periodFormatted = customData.period && customData.period.trim()
    const price = this.getFormattedTotalPrice(offer)

    return `${price}${periodFormatted ? ` ${periodFormatted}` : ""}`
  },

  hasExitIntentEligiblePrice(offer = this.values.activeOffer) {
    return (
      offer?.prices?.some((price) =>
        [PRICE_TYPES.EXIT_INTENT_TRIAL, PRICE_TYPES.EXIT_INTENT].includes(price.type),
      ) && this.isBasicOffer(offer)
    )
  },

  shouldShowExitIntentPopup() {
    const { exitIntent, showDiscountModal, activeOffer, trialExitIntent } = this.values
    const { exit_intent_discount, trial_exit_intent_price_discount } = activeOffer.customData || {}

    const isBasicOffer = this.isBasicOffer(activeOffer) || this.isBasicExitIntentOffer(activeOffer)

    // Exit intent is for basic offers only
    if (!isBasicOffer) return false

    if (this.isG2Variant() && !trialExitIntent && exitIntent) {
      const basicExitIntentOffer = pipelineConfig.offers.filter(
        (offer) => offer.type && offer.type.toLowerCase() === "basic_exit_intent",
      )
      this.activeOffer = basicExitIntentOffer[0]
      return true
    }

    // Exit if popup should not be shown
    if (
      exitIntent ||
      showDiscountModal ||
      !this.hasExitIntentEligiblePrice(activeOffer) ||
      trialExitIntent
    ) {
      return false
    }

    // Exit if basic offer has exit-intent discount, or if it's a trial basic offer with a trial exit-intent discount
    if (
      this.isBasicOffer(activeOffer) &&
      (exit_intent_discount || (this.isOfferTrial(activeOffer) && trial_exit_intent_price_discount))
    ) {
      return true
    }

    return false
  },

  getFbAdsId() {
    if (this.values.fb_ad_id) {
      return this.values.fb_ad_id
    }

    return this.values.fb_ads_id || undefined
  },

  async callPaymentsApi(offer, restrictSaveCard) {
    const url = new URL(pipelineConfig.paymentsApiUrl)
    url.pathname = "/api/v1/payments"

    const originUrl = new URL(window.location.origin.replace("localhost:8080", "lvh.me"))
    originUrl.pathname = pipelineConfig.pathPrefix

    const isBasic = this.isBasicOffer(offer) || this.isBasicExitIntentOffer(offer)
    const isExitIntent = this.isBasicExitIntentOffer(offer)
      ? false
      : isBasic && this.values.exitIntent

    return await fetch(url.toString(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        brandCode: pipelineConfig.brandCode,
        currencyCode: pipelineConfig.currencyCode,
        provider: "primer",
        source: "cppv3",
        customerId: this.values.customerId,
        orderId: this.values.id.toString(),
        orderToken: this.values.token,
        originUrl: originUrl.toString(),
        fbp: this.values._fbp,
        fbc: this.values._fbc,
        gaCid: this.values._ga?.match(/[^\.]+\.[^\.]+$/)?.[0],
        gaSessionId: this.getGA4SessionId(),
        utmCampaign: this.values.utm_campaign,
        utmSource: this.values.utm_source,
        utmMedium: this.values.utm_medium,
        utmTerm: this.values.utm_term,
        utmContent: this.values.utm_content,
        fraudNetSessionId: this.values.fraud_net_session_id,
        restrictSaveCard: restrictSaveCard || undefined,
        pipelineOfferType: offer.type,
        abExperimentPipelineId: this.values.abExperimentPipelineId || undefined,
        // TODO: For upsells, the offerPriceId may not always reflect
        // the correct price set in the backend.
        // To ensure reliability, it's only assigned for basic offers.
        ...(isBasic && {
          offerPriceId: this.getPriceProperty(this.getPriceType(this.values.activeOffer), "id"),
        }),
        trackingProperties: {
          fb_ads_id: this.getFbAdsId(),
          fb_adset_id: this.values.fb_adset_id || undefined,
          cookies: this.values.cookies || undefined,
          page: window.location.pathname.split(pipelineConfig.pathPrefix)[1].split("/")[0] || null,
          page_version: this.getPageVersion(),
          pipe_series: pipelineConfig.series || undefined,
          ...getPaymentQueryString(this),
        },
        ...(offer.brandOfferId
          ? {
              chance: isExitIntent,
              offerId: offer.brandOfferId,
            }
          : {
              priceProductObjectId: isExitIntent
                ? offer.exitIntentPriceProductObjectId
                : offer.priceProductObjectId,
            }),
      }),
    }).then((data) => data.json())
  },

  async payOrCreateSession(restrictSaveCard) {
    const response = await this.callPaymentsApi(this.values.activeOffer, restrictSaveCard)

    if (response.clientToken) {
      this.values.primerSession = response
    } else {
      this.values.primerSession = null
    }

    return response
  },

  clearPrimerSession() {
    this.values.preloadedPrimerSessions = null
    this.values.primerSession = null
  },

  resetLocalStorage() {
    this.values = initialState()
    this.storeAnalytics()

    if (sessionStorage.getItem("klaviyoTrackEventsList"))
      sessionStorage.removeItem("klaviyoTrackEventsList")
  },

  buildUrlParamsForThankYou(clientSession) {
    const { trackingEvent } = clientSession
    const params = new URLSearchParams({
      invoice_id: trackingEvent?.messageId,
      invoice_amount: trackingEvent?.amountInEuro,
      ga_transaction_id: trackingEvent?.messageId,
      order_id: trackingEvent ? this.values.id : undefined,
      ga_currency: trackingEvent?.currency,
      ga_value: trackingEvent?.amount,
    })

    if (trackingEvent?.pipelineOfferType) {
      params.set("pipeline_offer_type", trackingEvent.pipelineOfferType)
    } else if (trackingEvent?.offerType) {
      params.set("offer_type", trackingEvent.offerType)
    }

    return params
  },

  async goToLogin() {
    await this.pull(true)
    const loginUrl = this.values.loginUrl
    if (loginUrl) {
      this.values = initialState()
      window.location = loginUrl || pipelineConfig.loginUrl
    }
  },

  savePurchasedBasicOffer() {
    if (!this.values.purchasedBasicOffer) {
      this.values.purchasedBasicOffer = this.values.activeOffer
    }
  },

  restorePurchasedBasicOffer() {
    if (this.values.purchasedBasicOffer) {
      this.values.activeOffer = this.values.purchasedBasicOffer
      this.values.purchasedBasicOffer = null
    }
  },

  isErrorBannerAllowed() {
    const forbiddenPaths = ["16-create-plan"]
    const pagePaths = pipelineConfig.variants[pipelineConfig.variant].pagePaths
    if (pagePaths) {
      const isForbiddenPath = forbiddenPaths.some((path) =>
        window.location.pathname.includes(pagePaths[path]),
      )
      if (isForbiddenPath) {
        return false
      }
    }

    return true
  },
  // Most of above code from u-series with exit intent modifications
  // Functions from d-series
  getFromLocalStorage(key) {
    const val = getStoreByKeyValue(LOCAL_STORAGE_KEY, key)
    return typeof val === "string" ? val : null
  },

  formattedTargetWeight() {
    const { targetWeightKg, targetWeightLbs } = this.values

    return this.formatWeight(this.isMetric ? targetWeightKg : targetWeightLbs)
  },

  formattedWeightForChatBubble() {
    const weightMetric = "2"
    const weightImperial = "4"

    return this.formatWeight(this.isMetric ? weightMetric : weightImperial)
  },

  isFFPipeline() {
    return pipelineConfig.variant === "FunnelFox"
  },

  isG2Variant() {
    return pipelineConfig.variant === "G2"
  },

  getPromoCode() {
    return getPromoCode(this.values)
  },
}
